import { JsonPipe, KeyValuePipe } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  DemoFieldsWithAutocompleteAddress,
  DemoFieldsWithEditableAddress,
  DemoFieldsWithReadonlyAddress,
} from '@demo/data/demo-cipo-fields';
import { AddressModel } from '@models/module/fields/main';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { CipoAddressComponent } from '@shared/components/fields/cipo-address/cipo-address.component';
import { CipoAddressControl } from '@shared/components/fields/common';

@Component({
  selector: 'app-demo-address-autocomplete',
  templateUrl: './demo-address-autocomplete.component.html',
  styles: ':host{display: block;overflow: auto;height: 100%;}',
  imports: [CipoAddressComponent, FlexLayoutModule, KeyValuePipe, JsonPipe, MatButton],
})
export class DemoAddressAutocompleteComponent {
  fb = inject(FormBuilder);
  formReadonly = signal(this.fb.group<{ [x: string]: CipoAddressControl }>({}));
  formEditable = signal(this.fb.group<{ [x: string]: CipoAddressControl }>({}));
  formAutocomplete = signal(this.fb.group<{ [x: string]: CipoAddressControl }>({}));
  lastValueChanged = signal<AddressModel | AddressModel[]>(null);

  constructor() {
    this.addFieldsToForm(this.formReadonly(), DemoFieldsWithReadonlyAddress);
    this.addFieldsToForm(this.formEditable(), DemoFieldsWithEditableAddress);
    this.addFieldsToForm(this.formAutocomplete(), DemoFieldsWithAutocompleteAddress);
  }

  markAsTouched(touched: boolean) {
    [this.formReadonly(), this.formEditable(), this.formAutocomplete()].forEach(form => {
      if (touched) {
        form.markAllAsTouched();
      } else {
        form.markAsUntouched();
      }
      form.updateValueAndValidity();
    });
  }

  private addFieldsToForm(
    form: FormGroup<{ [x: string]: CipoAddressControl }>,
    fields: typeof DemoFieldsWithReadonlyAddress,
  ) {
    fields
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach(field => {
        const { name, defaultValue, ...props } = field;
        const control = new CipoAddressControl(defaultValue, props);
        this.bindLastChangedValue(control);
        form.addControl(name, control);
      });
  }

  private bindLastChangedValue(control: CipoAddressControl) {
    control.valueChanges.subscribe(value => {
      if (!control.touched) {
        return;
      }
      this.lastValueChanged.set(value ?? null);
    });
  }
}
